import type { ShouldRevalidateFunctionArgs } from 'react-router';

import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18N_LOCALES } from '#/i18n/types/I18nLocale';
import type {
  LayoutHandleElements,
  LayoutHandleProps,
} from '#/layouts/Layout/LayoutHandleProps';
import { CreateListingApplicationFromTmpStorageContainer } from '#/modules/listing/containers/CreateListingApplicationFromTmpStorageContainer';
import { isListingExpired } from '#/modules/listing/helpers/listingTiming';
import { viewVolopReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { listingApiServerFetchVolop } from '#/modules/listing/volops/api/listingApiFetchVolop';
import { DynamicSearchVolopsRefinements } from '#/modules/search/volops/components/Refinements/DynamicSearchVolopsRefinements';
import { userDashboardApiServerFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { DynamicNotFoundPage } from '#/pages/NotFound/DynamicNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { dynamic } from '#/utils/dynamic';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewVolopPage } from './ViewVolopPage';

const DynamicExpiredVolopPage = dynamic(
  () =>
    import('#/modules/listing/volops/pages/expired/ExpiredVolopPage').then(
      (m) => ({ default: m.ExpiredVolopPage }),
    ),
  { strategy: 'always' },
);

const DynamicUnpublishedVolopPage = dynamic(
  () =>
    import('#/modules/listing/volops/pages/unpublished/UnpublishedVolopPage').then(
      (m) => ({ default: m.UnpublishedVolopPage }),
    ),
  { strategy: 'always' },
);

export const handle = {
  searchRefinements: (
    <DynamicSearchVolopsRefinements includeSourcesFacet={false} />
  ),
} satisfies LayoutHandleElements;

export function shouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  const removedListingApplicationTmpId =
    currentUrl.searchParams.has('listingApplicationTmpId') &&
    !nextUrl.searchParams.has('listingApplicationTmpId');

  if (removedListingApplicationTmpId) return false;

  return defaultShouldRevalidate;
}

export async function loader({
  request,
  context,
  params: { volopIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewVolopReferenceRoute>) {
  const { serverContext } = getServerRouterContext(context);

  const sharedHandle = {
    defaultSearchType: 'VOLOP',
    showDesktopSearch: true,
    showSearchRefinements: true,
  } satisfies LayoutHandleProps;

  const [volopId] = volopIdDashSlug.split('-');

  try {
    const [volop, listingExtras] = await Promise.all([
      listingApiServerFetchVolop({ request, context })(volopId),
      serverContext.hasNoCacheCookie
        ? userDashboardApiServerFetchListingExtras({
            request,
            context,
          })([volopId])
        : null,
    ]);

    const { saved, applicationId } = listingExtras?.[volopId] ?? {
      saved: false,
      applicationId: null,
    };

    switch (volop.type) {
      case 'VOLOP': {
        return {
          type: isListingExpired({
            expires: volop.expires,
            now: serverContext.now,
          })
            ? 'EXPIRED_VOLOP'
            : 'VOLOP',
          volop,
          applicationId,
          saved,
          locale: serverContext.locale,
          handle: {
            ...sharedHandle,
            hideFooterDivider: 'hide-footer-divider-if-logged-out',
            searchFromPageType: 'LISTING',
            urlByLocale: volop.url,
          } satisfies LayoutHandleProps,
          now: serverContext.now,
        } as const;
      }
      case 'UNPUBLISHED_VOLOP':
        return idealistNotFound({
          type: volop.type,
          unpublishedVolop: volop,
          handle: sharedHandle,
        } as const);
    }
  } catch {
    return idealistNotFound({
      type: 'NOT_FOUND',
      handle: sharedHandle,
    } as const);
  }
}

export default function ViewVolopHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  switch (loaderData.type) {
    case 'NOT_FOUND':
      return <DynamicNotFoundPage />;
    case 'VOLOP':
      return (
        <RouteModule
          title={`${loaderData.volop.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['VOLOP']}`}
          ogImage={
            loaderData.volop.isPostedAnonymously
              ? null
              : imageUrlForOgMeta({
                  data: loaderData.volop.org?.logo,
                })
          }
          description={loaderData.volop.descriptionTruncated.slice(0, 200)}
          linkRels={{
            translations: I18N_LOCALES.map((locale) => {
              const url = loaderData.volop.url[locale];
              if (url) return { url, locale };
            }).filter(Boolean),
            canonical: loaderData.volop.url[loaderData.volop.locale],
          }}
          schemaMarkups={[
            loaderData.volop.schemaVolopMarkup,
            loaderData.volop.schemaBreadcrumbListMarkup?.[loaderData.locale],
          ]}
        >
          <CreateListingApplicationFromTmpStorageContainer
            listing={loaderData.volop}
          >
            {({ initialListingApplicationId, listingApplicationFailure }) => (
              <ViewVolopPage
                volop={loaderData.volop}
                listingApplicationId={
                  initialListingApplicationId ?? loaderData.applicationId
                }
                initiallySaved={loaderData.saved}
                listingApplicationFailure={listingApplicationFailure}
                now={loaderData.now}
              />
            )}
          </CreateListingApplicationFromTmpStorageContainer>
        </RouteModule>
      );
    case 'EXPIRED_VOLOP':
      return (
        <RouteModule title={loaderData.volop.name}>
          <DynamicExpiredVolopPage
            volop={loaderData.volop}
            listingApplicationId={loaderData.applicationId}
            now={loaderData.now}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_VOLOP':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <DynamicUnpublishedVolopPage
            unpublishedVolop={loaderData.unpublishedVolop}
          />
        </RouteModule>
      );
  }
}
